import { useCallback, useState } from "react";

/**
 * The grid's page index, remembered against the search string it was chosen
 * under and read back as 0 for any other string.
 *
 * A filter change makes the old page index meaningless: it usually points past
 * the end of the new, smaller result set. MUI only finds that out once the
 * response lands — it then clamps to page 0 and asks again — so leaving the page
 * alone costs a full extra query per search, and the wasted one is the slow one
 * (a deep OFFSET over the whole filtered set). Deriving the page rather than
 * resetting it from an effect means the first request for a new search already
 * asks for page 0: one query per settled search instead of two.
 *
 * Returning to a string you had paged through restores that page, which is what
 * you want when a filter chip is toggled off and back on.
 */
export function usePageForSearch(search: string): {
  page: number;
  setPage: (page: number) => void;
} {
  const [pageForSearch, setPageForSearch] = useState({ search, page: 0 });

  const setPage = useCallback(
    (page: number) => {
      setPageForSearch({ search, page });
    },
    [search]
  );

  return { page: pageForSearch.search === search ? pageForSearch.page : 0, setPage };
}
